// 피드백 창 (v2 client/1-endpoints/screen/core/feedback.ts에서 옮김). 사용:
//  1) openFeedback({ name, place, canvas }) — 창을 연다 (canvas = 게임 캔버스, 없어도 됨)   2) isFeedbackOpen()·closeFeedback() — 열림 확인·닫기
//  3) 열려 있는 동안 게임 입력은 잠근다(G.input.locked). 키 막기(G.keyGuard)와 Esc는 도구 막대(meeting/toolbar.ts)가 esc.ts에 등록한다: core는 meeting을 가져올 수 없다.
//  창 안의 keydown은 window의 일반 리스너로 올라가지 않게 막아 둔다.
import { FEEDBACK_IMG_MAX, FEEDBACK_TEXT_MAX } from '../../../../shared/limits';

const G = (window as any).G;

const TEXT_MAX = FEEDBACK_TEXT_MAX, IMG_MAX = FEEDBACK_IMG_MAX, SHOT_W = 960;
let root = null, prevFocus = null, vvOff = null;

const CSS = `
.fb-root{position:fixed;left:0;top:0;width:100%;height:100%;z-index:99999;display:flex;align-items:center;justify-content:center;padding:12px;background:rgba(40,28,48,.55);font-family:var(--ui-font,inherit);color:var(--ui-ink,#3b2d44);-webkit-text-size-adjust:100%}
.fb-root *{box-sizing:border-box;font-family:inherit}
.fb-card{width:min(440px,100%);max-height:100%;overflow:auto;background:var(--ui-panel,#fffaf0);border:3px solid var(--ui-line,#3b2d44);border-radius:var(--ui-radius,10px);box-shadow:4px 4px 0 var(--ui-line,#3b2d44);padding:16px;display:flex;flex-direction:column;gap:12px}
.fb-card h2{margin:0;font-size:18px;line-height:1.2}
.fb-seg{display:flex;gap:6px}
.fb-seg input{position:absolute;opacity:0;pointer-events:none}
.fb-seg label{flex:1;text-align:center;padding:8px 6px;border:2px solid var(--ui-line,#3b2d44);border-radius:var(--ui-radius,10px);cursor:pointer;font-weight:700;font-size:14px;background:transparent;user-select:none}
.fb-seg input:checked+label{background:var(--ui-accent,#ffd34d)}
.fb-seg input:focus-visible+label{outline:3px solid var(--ui-muted,#8a7f99);outline-offset:1px}
.fb-card textarea{width:100%;height:132px;resize:none;padding:10px;font-size:16px;line-height:1.45;color:inherit;background:#fff;border:2px solid var(--ui-line,#3b2d44);border-radius:var(--ui-radius,10px)}
.fb-card textarea:focus{outline:3px solid var(--ui-accent,#ffd34d);outline-offset:0}
.fb-count{align-self:flex-end;margin-top:-8px;font-size:12px;color:var(--ui-muted,#8a7f99)}
.fb-shot{display:flex;gap:10px;align-items:center;font-size:14px;cursor:pointer}
.fb-shot input{width:18px;height:18px;accent-color:var(--ui-line,#3b2d44);flex:none}
.fb-shot img{height:44px;max-width:96px;object-fit:contain;border:2px solid var(--ui-line,#3b2d44);border-radius:6px;background:#fff;margin-left:auto}
.fb-row{display:flex;gap:8px;justify-content:flex-end}
.fb-row button{font:inherit;font-size:15px;font-weight:700;padding:9px 18px;border:2px solid var(--ui-line,#3b2d44);border-radius:var(--ui-radius,10px);color:inherit;background:var(--ui-panel,#fffaf0);cursor:pointer}
.fb-row .fb-go{background:var(--ui-accent,#ffd34d);box-shadow:2px 2px 0 var(--ui-line,#3b2d44)}
.fb-row button:disabled{opacity:.5;cursor:default}
.fb-st{min-height:1.3em;font-size:13px;line-height:1.3;color:var(--ui-muted,#8a7f99)}
.fb-st.err{color:#c0392b;font-weight:700}.fb-st.ok{color:#2e7d4f;font-weight:700}
@media (max-width:520px){.fb-root{align-items:flex-end;padding:0}.fb-card{width:100%;border-radius:var(--ui-radius,10px) var(--ui-radius,10px) 0 0;border-width:3px 0 0;box-shadow:none;padding-bottom:max(16px,env(safe-area-inset-bottom))}}
`;

// 캔버스 → JPEG data URL. 960px보다 작으면 정수 배로 키워 또렷하게, 크면 줄인다. 너무 크면 화질을 낮춘다.
function shot(canvas) {
  try {
    if (!canvas || !canvas.width || !canvas.height) return null;
    const k = canvas.width >= SHOT_W ? SHOT_W / canvas.width : Math.floor(SHOT_W / canvas.width);
    const c = document.createElement('canvas');
    c.width = Math.round(canvas.width * k); c.height = Math.round(canvas.height * k);
    const g = c.getContext('2d');
    g.fillStyle = '#fff'; g.fillRect(0, 0, c.width, c.height);
    g.imageSmoothingEnabled = k < 1;
    g.drawImage(canvas, 0, 0, c.width, c.height);
    for (const q of [0.7, 0.5, 0.35]) { const u = c.toDataURL('image/jpeg', q); if (u.length <= IMG_MAX) return u; }
  } catch {}  // 캔버스가 오염됐거나 메모리가 모자라면 사진 없이
  return null;
}

function close() {
  if (!root) return;
  root.remove(); root = null; G.input.locked = false;
  if (vvOff) { vvOff(); vvOff = null; }
  try { prevFocus?.focus?.({ preventScroll: true }); } catch {}
}

function open(ctx: any = {}) {
  if (root) return;
  if (!document.getElementById('fb-style')) {
    const st = document.createElement('style'); st.id = 'fb-style'; st.textContent = CSS; document.head.appendChild(st);
  }
  prevFocus = document.activeElement; (prevFocus as HTMLElement | null)?.blur?.(); G.input.locked = true;
  const img = shot(ctx.canvas);
  root = document.createElement('div');
  root.className = 'fb-root';
  root.setAttribute('role', 'dialog'); root.setAttribute('aria-modal', 'true'); root.setAttribute('aria-label', '피드백 보내기');
  root.innerHTML = `<form class="fb-card" novalidate>
<h2>피드백 보내기</h2>
<div class="fb-seg" role="radiogroup"><input type="radio" name="fbk" id="fbk-bug" value="bug" checked><label for="fbk-bug">버그</label><input type="radio" name="fbk" id="fbk-idea" value="idea"><label for="fbk-idea">개선 제안</label></div>
<textarea maxlength="${TEXT_MAX}" placeholder="어떤 일이 있었나요? 무엇이 바뀌면 좋을까요?" aria-label="내용"></textarea>
<div class="fb-count">0 / ${TEXT_MAX}</div>
${img ? '<label class="fb-shot"><input type="checkbox" checked><span>지금 게임 화면 첨부</span><img alt="화면 미리보기"></label>' : ''}
<div class="fb-st" role="status" aria-live="polite"></div>
<div class="fb-row"><button type="button" class="fb-x">취소</button><button type="submit" class="fb-go">보내기</button></div>
</form>`;
  const $ = (s) => root.querySelector(s);
  const ta = $('textarea'), st = $('.fb-st'), go = $('.fb-go'), cnt = $('.fb-count'), chk = $('.fb-shot input');
  if (img) $('.fb-shot img').src = img;
  const say = (m, cls = '') => { st.textContent = m; st.className = 'fb-st ' + cls; };

  // 창 안의 키는 게임에 닿지 않게 한다 (게임 keydown은 window에 있다). keyup은 막지 않는다: 누른 채 창을 열면 키가 눌린 채로 굳는다.
  root.addEventListener('keydown', (e) => { e.stopPropagation(); });   // Esc는 esc.ts가 window 캡처 단계에서 먼저 받아 닫는다
  root.addEventListener('keypress', (e) => e.stopPropagation());
  root.addEventListener('pointerdown', (e) => { if (e.target === root) close(); });
  $('.fb-x').onclick = close;
  ta.addEventListener('input', () => { cnt.textContent = `${ta.value.length} / ${TEXT_MAX}`; });

  $('.fb-card').addEventListener('submit', async (e) => {
    e.preventDefault();
    const text = ta.value.trim();
    if (!text) { say('내용을 적어 주세요', 'err'); ta.focus(); return; }
    go.disabled = true; say('보내는 중…');
    try {
      const r = await fetch('/feedback', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ kind: $('input[name=fbk]:checked').value, text, name: ctx.name || '', place: ctx.place || '', img: img && chk.checked ? img : undefined }),
      });
      const j = await r.json().catch(() => ({}));
      if (r.ok && j.ok) { say('고마워요! 잘 받았습니다', 'ok'); ta.disabled = true; setTimeout(close, 1600); return; }
      say(r.status === 429 ? '1분 뒤에 다시 보내 주세요' : r.status === 413 ? '화면 사진이 너무 큽니다. 첨부를 끄고 다시 보내 주세요' : j.error || '보내지 못했습니다. 잠시 뒤 다시 시도해 주세요', 'err');
    } catch { say('연결이 끊겼습니다. 잠시 뒤 다시 시도해 주세요', 'err'); }
    go.disabled = false;
  });

  // 모바일 키보드가 올라오면 창을 보이는 영역에 맞춘다
  const vv = window.visualViewport;
  if (vv) {
    const fit = () => { root.style.top = vv.offsetTop + 'px'; root.style.left = vv.offsetLeft + 'px'; root.style.height = vv.height + 'px'; root.style.width = vv.width + 'px'; };
    vv.addEventListener('resize', fit); vv.addEventListener('scroll', fit); fit();
    vvOff = () => { vv.removeEventListener('resize', fit); vv.removeEventListener('scroll', fit); };
  }
  document.body.appendChild(root);
  ta.focus({ preventScroll: true });
}

export const isFeedbackOpen = () => !!root;
export { open as openFeedback, close as closeFeedback };
